// ===================== Renderer — 2.5D scene =====================
import { CONFIG } from '../config.js';
import { Settings } from '../engine/settings.js';
import { getSprite } from '../content/sprites.js';
import { hash2, TAU, clamp01 } from '../engine/math.js';

const DECOR_SHADOW = 'rgba(0,0,0,0.28)';

export function renderScene(game) {
  const { ctx, camera: cam } = game;
  const w = cam.w, h = cam.h;
  // Bloom rides the particle-quality switch: 'low' is the low-end escape
  // hatch, and the bloom pass is pure spend. Exposed on the game object so the
  // smoke test can assert the gate without screenshot forensics.
  const bloomOn = Settings.particles !== 'low' && w > 0 && h > 0;
  game.bloomActive = bloomOn;

  drawBackground(ctx, game, w, h);
  drawGround(ctx, game);
  drawShadows(ctx, game);
  drawPickups(ctx, game);
  drawEntities(ctx, game);
  // Biome darkness sits UNDER every emissive layer: projectiles, bullets,
  // particles, damage numbers and the player marker all draw after it at full
  // brightness, so mood can never tax the readability layers.
  drawDarkness(ctx, game, w, h);
  drawProjectiles(ctx, game);
  // The bloom composite lands here — above the emissive world layers it
  // mirrors, below the reserved readability layers (enemy-bullet contours,
  // damage numbers, the player marker), which therefore stay crisp.
  if (bloomOn) drawBloom(ctx, game, w, h);
  drawEnemyBullets(ctx, game);
  drawParticles(ctx, game);
  drawPlayerMarker(ctx, game);
  drawBossArrow(ctx, game);
  drawLootPings(ctx, game);
  drawVignette(ctx, game, w, h);
  drawFlash(ctx, game, w, h);
  // The combo counter reports where the top-of-screen chrome actually ends, so
  // the announce line can be placed clear of the HUD *and* of a live streak.
  drawAnnounce(ctx, game, w, h, drawCombo(ctx, game, w, h));
}

// Full-screen impact flash (boss spawn/kill, big events).
function drawFlash(ctx, game, w, h) {
  if (!game.flash || game.flash <= 0) return;
  ctx.save();
  ctx.globalCompositeOperation = 'lighter';
  ctx.globalAlpha = Math.min(0.5, game.flash * 0.5);
  ctx.fillStyle = game.flashColor;
  ctx.fillRect(0, 0, w, h);
  ctx.restore();
}

// Kill-streak combo counter. Returns the bottom of the top-of-screen chrome —
// the HUD, plus this counter whenever a streak is live — for drawAnnounce.
function drawCombo(ctx, game, w, h) {
  const hudBottom = game.hudBottom || 96;
  if (game.combo < 5) return hudBottom;
  const t = clamp01(game.comboTimer / 3);
  const scale = 1 + Math.min(game.combo, 120) / 120;
  const col = game.combo >= 50 ? '#ff5cf0' : game.combo >= 25 ? '#ffd15c' : '#7df9ff';
  // Cyan/gold/magenta are one colour to a deuteranope, so the tier also
  // carries a star count — the text is otherwise identical between tiers.
  const tier = game.combo >= 50 ? ' ✦✦' : game.combo >= 25 ? ' ✦' : '';
  const label = game.combo + ' COMBO' + tier;
  // Sit below the DOM HUD, whose height varies (rows wrap on phones, slot rows
  // grow with ultraweapons). `game.hudBottom` is the HUD's measured bottom in
  // CSS px — the same unit the canvas transform uses. Top-baselined, so the
  // clearance holds even as `scale` doubles the font on a long streak; clamped
  // so a runaway HUD can never push the counter into the play area.
  const y = Math.min(hudBottom + 10, h * 0.45);
  const size = Math.round(20 * scale);
  ctx.save();
  ctx.globalAlpha = 0.5 + 0.5 * t;
  ctx.font = `bold ${size}px 'Courier New', monospace`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  ctx.fillStyle = 'rgba(0,0,0,0.55)';
  ctx.fillText(label, w / 2 + 2, y + 2);
  ctx.fillStyle = col;
  ctx.fillText(label, w / 2, y);
  ctx.restore();
  ctx.textAlign = 'left';
  // The em box, a safe over-estimate of the painted descent at either scale.
  return y + size;
}

// Arrow pointing to the boss when it's off-screen.
function drawBossArrow(ctx, game) {
  const b = game.boss;
  if (!b || b.dead) return;
  const cam = game.camera;
  if (cam.visible(b.x, b.y, b.r)) return;
  const s = cam.worldToScreen(b.x, b.y);
  const cx = cam.w / 2, cy = cam.h / 2;
  const ang = Math.atan2(s.y - cy, s.x - cx);
  const rx = cam.w / 2 - 48, ry = cam.h / 2 - 60;
  const ex = cx + Math.cos(ang) * rx, ey = cy + Math.sin(ang) * ry;
  ctx.save();
  ctx.translate(ex, ey);
  ctx.rotate(ang);
  ctx.fillStyle = '#ff2d9b';
  ctx.beginPath();
  ctx.moveTo(17, 0); ctx.lineTo(-11, -12); ctx.lineTo(-11, 12); ctx.closePath();
  ctx.fill();
  ctx.restore();
  ctx.save();
  ctx.globalAlpha = 0.9;
  ctx.font = "bold 11px 'Courier New', monospace";
  ctx.textAlign = 'center';
  ctx.fillStyle = '#ff8ac0';
  ctx.fillText('BOSS', ex, ey - 20);
  ctx.restore();
  ctx.textAlign = 'left';
}

// Edge pings for high-value loot (chests, Multiplication) sitting off-screen,
// so a drop you kited away from is never silently lost.
function drawLootPings(ctx, game) {
  const cam = game.camera;
  let shown = 0;
  for (const g of game.pickups.active) {
    if (g.dead || (g.kind !== 'chest' && g.kind !== 'multiply')) continue;
    if (cam.visible(g.x, g.y, 40)) continue;
    if (++shown > 4) break;                      // don't wallpaper the edges
    const s = cam.worldToScreen(g.x, g.y);
    const cx = cam.w / 2, cy = cam.h / 2;
    const ang = Math.atan2(s.y - cy, s.x - cx);
    const ex = cx + Math.cos(ang) * (cam.w / 2 - 40);
    const ey = cy + Math.sin(ang) * (cam.h / 2 - 52);
    const pulse = 0.55 + 0.35 * Math.sin(game.elapsed * 6 + shown * 2);
    ctx.save();
    ctx.globalAlpha = pulse;
    ctx.translate(ex, ey);
    ctx.rotate(ang);
    ctx.fillStyle = g.kind === 'chest' ? '#ffd15c' : '#b98cff';
    ctx.beginPath();
    ctx.moveTo(12, 0); ctx.lineTo(-7, -8); ctx.lineTo(-7, 8); ctx.closePath();
    ctx.fill();
    ctx.restore();
    const spr = getSprite(g.kind);
    if (spr) {
      const hh = 20, ww = hh * (spr.w / spr.h);
      const ix = ex - Math.cos(ang) * 24, iy = ey - Math.sin(ang) * 24;
      ctx.save();
      ctx.imageSmoothingEnabled = false;
      ctx.globalAlpha = 0.9;
      ctx.drawImage(spr.canvas, ix - ww / 2, iy - hh / 2, ww, hh);
      ctx.restore();
    }
  }
}

// ------------------------------------------------------------- background
// The backdrop gradient is rebuilt only when the stage or canvas height
// changes (like the vignette cache below) — building it per frame was the
// exact per-frame-gradient regression the glow cache exists to prevent.
let _bgKey = '', _bgGrad = null;
function bgGradient(ctx, st, h) {
  const key = st.id + ':' + h;
  if (key !== _bgKey) {
    _bgKey = key;
    _bgGrad = ctx.createLinearGradient(0, 0, 0, h);
    _bgGrad.addColorStop(0, shade(st.ground.a, -0.5));
    _bgGrad.addColorStop(1, shade(st.ground.a, -0.28));
  }
  return _bgGrad;
}

function drawBackground(ctx, game, w, h) {
  const st = game.stage;
  ctx.fillStyle = bgGradient(ctx, st, h);
  ctx.fillRect(0, 0, w, h);

  // Parallax dot field for depth.
  const cam = game.camera;
  const px = -cam.x * 0.25, py = -cam.y * 0.25;
  ctx.fillStyle = 'rgba(255,255,255,0.05)';
  const S = 140;
  const ox = ((px % S) + S) % S, oy = ((py % S) + S) % S;
  for (let x = -S; x < w + S; x += S) {
    for (let y = -S; y < h + S; y += S) {
      const hx = Math.floor((x - ox) / S), hy = Math.floor((y - oy) / S);
      const r = hash2(hx, hy, 7);
      if (r < 0.5) {
        const s = 1 + r * 3;
        ctx.globalAlpha = 0.04 + r * 0.05;
        ctx.fillRect(x + ox, y + oy, s, s);
      }
    }
  }
  ctx.globalAlpha = 1;
}

// ------------------------------------------------------------- ground
function drawGround(ctx, game) {
  const cam = game.camera;
  const st = game.stage;
  const ts = CONFIG.tileSize;
  const zoom = cam.zoom;
  const halfW = cam.w / 2 / zoom, halfH = cam.h / 2 / zoom;
  const left = cam.x - halfW, right = cam.x + halfW;
  const top = cam.y - halfH, bottom = cam.y + halfH;
  const cx0 = Math.floor(left / ts) - 1, cx1 = Math.floor(right / ts) + 1;
  const cy0 = Math.floor(top / ts) - 1, cy1 = Math.floor(bottom / ts) + 1;

  // Tiles. (Scalar worldToScreenX/Y throughout — the tile loops run hundreds
  // of times per frame and the object-returning variant allocates per call.)
  for (let cx = cx0; cx <= cx1; cx++) {
    const sx = cam.worldToScreenX(cx * ts);
    for (let cy = cy0; cy <= cy1; cy++) {
      const sy = cam.worldToScreenY(cy * ts);
      const size = ts * zoom + 1;
      const alt = ((cx + cy) & 1) === 0;
      ctx.fillStyle = alt ? st.ground.a : st.ground.b;
      ctx.fillRect(sx, sy, size, size);
    }
  }
  // Subtle per-tile brightness variation — breaks the checkerboard monotony.
  ctx.fillStyle = 'rgba(255,255,255,0.028)';
  for (let cx = cx0; cx <= cx1; cx++) {
    for (let cy = cy0; cy <= cy1; cy++) {
      if (hash2(cx, cy, 5) < 0.16) {
        ctx.fillRect(cam.worldToScreenX(cx * ts), cam.worldToScreenY(cy * ts), ts * zoom + 1, ts * zoom + 1);
      }
    }
  }
  ctx.fillStyle = 'rgba(0,0,0,0.06)';
  for (let cx = cx0; cx <= cx1; cx++) {
    for (let cy = cy0; cy <= cy1; cy++) {
      if (hash2(cx, cy, 5) > 0.86) {
        ctx.fillRect(cam.worldToScreenX(cx * ts), cam.worldToScreenY(cy * ts), ts * zoom + 1, ts * zoom + 1);
      }
    }
  }
  // Grid lines.
  ctx.strokeStyle = st.ground.grid;
  ctx.lineWidth = 1;
  ctx.beginPath();
  const gy0 = cam.worldToScreenY(top), gy1 = cam.worldToScreenY(bottom);
  for (let cx = cx0; cx <= cx1 + 1; cx++) {
    const gx = cam.worldToScreenX(cx * ts);
    ctx.moveTo(gx, gy0); ctx.lineTo(gx, gy1);
  }
  const gx0 = cam.worldToScreenX(left), gx1 = cam.worldToScreenX(right);
  for (let cy = cy0; cy <= cy1 + 1; cy++) {
    const gy = cam.worldToScreenY(cy * ts);
    ctx.moveTo(gx0, gy); ctx.lineTo(gx1, gy);
  }
  ctx.stroke();

  // Deterministic decorations (endless & stable as you move).
  const decor = st.decor;
  for (let cx = cx0; cx <= cx1; cx++) {
    for (let cy = cy0; cy <= cy1; cy++) {
      const r = hash2(cx, cy, CONFIG.worldSeed);
      if (r > st.decorDensity) continue;
      const rx = hash2(cx, cy, 11), ry = hash2(cx, cy, 23);
      const wx = cx * ts + rx * ts, wy = cy * ts + ry * ts;
      const key = decor[Math.floor(hash2(cx, cy, 31) * decor.length)];
      const size = 18 + hash2(cx, cy, 41) * 16;
      blitSprite(ctx, cam, key, wx, wy, size * zoom, { shadow: true, anchor: 0.15 });
    }
  }

  // Biome fog tint.
  if (st.fog && st.fog !== 'rgba(20,10,40,0.0)') {
    ctx.fillStyle = st.fog;
    ctx.fillRect(0, 0, cam.w, cam.h);
  }

  // Soft light pool around the player — sells the 2.5D lighting.
  if (game.player) {
    const px = cam.worldToScreenX(game.player.x), py = cam.worldToScreenY(game.player.y);
    const gr = 360 * zoom;
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    ctx.globalAlpha = 0.09;
    ctx.drawImage(glowSprite('#bcd2ff'), px - gr, py - gr, gr * 2, gr * 2);
    ctx.restore();
  }
}

// ------------------------------------------------------------- shadows
// One path, one fill for the whole field: every shadow shares one fillStyle,
// and per-entity beginPath/fill was ~2 calls × live entities per frame. As a
// bonus, overlapping shadows no longer double-darken where hordes bunch.
function drawShadows(ctx, game) {
  const cam = game.camera;
  ctx.fillStyle = 'rgba(0,0,0,0.30)';
  ctx.beginPath();
  const addShadow = (x, y, r) => {
    if (!cam.visible(x, y, r + 40)) return;
    // Scalar transforms — one shadow per live entity per frame.
    const sx = cam.worldToScreenX(x), sy = cam.worldToScreenY(y);
    const cy = sy + r * 0.3 * cam.zoom, rx = r * 0.95 * cam.zoom;
    ctx.moveTo(sx + rx, cy);   // detach the subpath — ellipse() draws from the pen
    ctx.ellipse(sx, cy, rx, r * 0.42 * cam.zoom, 0, 0, TAU);
  };
  for (const e of game.enemies.active) if (!e.dead) addShadow(e.x, e.y, e.r);
  addShadow(game.player.x, game.player.y, game.player.r);
  ctx.fill();
}

// ------------------------------------------------------------- pickups
// Gems are drawn in two passes: every additive halo inside ONE 'lighter'
// block, then every body. The old shape saved/restored the context and
// toggled globalCompositeOperation twice *per gem*, and a late run has
// hundreds of them live (profiled at ~630 gems: 7.5ms → 5.9ms median for the
// pickup pass, ~21%). Reused scratch buffers — no per-frame allocation.
const _gemXYR = [];        // x, y, r, coin quads (numbers only)
const _gemCol = [];        // parallel colour strings
const _haloCache = new Map();
function haloColor(c) {
  // hexA() builds a string; caching keeps the halo colour out of the hot loop.
  let s = _haloCache.get(c);
  if (s === undefined) { s = hexA(c, 0.30); _haloCache.set(c, s); }
  return s;
}

// The additive gem-halo pass, over whatever _gemXYR/_gemCol currently hold.
// Split out of drawPickups so the bloom pass can restamp the same field into
// its quarter-res buffer without re-walking (or re-transforming) the pickups.
function drawGemHalos(ctx) {
  ctx.save();
  ctx.globalCompositeOperation = 'lighter';
  for (let i = 0, j = 0; i < _gemXYR.length; i += 4, j++) {
    ctx.fillStyle = haloColor(_gemCol[j]);
    ctx.beginPath(); ctx.arc(_gemXYR[i], _gemXYR[i + 1], _gemXYR[i + 2] * 1.8, 0, TAU); ctx.fill();
  }
  ctx.restore();
}

function drawPickups(ctx, game) {
  const cam = game.camera;
  const t = game.elapsed;
  const z = cam.zoom;
  _gemXYR.length = 0;
  _gemCol.length = 0;
  // Pass 0 — collect gems; anything sprite-shaped is deferred so it keeps
  // reading clearly on top of a dense gem field.
  for (const g of game.pickups.active) {
    if (g.dead || !cam.visible(g.x, g.y, 40)) continue;
    if (g.kind !== 'xp' && g.kind !== 'gold') continue;
    // Scalar transforms — gems can number in the hundreds.
    const coin = g.kind === 'gold';
    _gemXYR.push(
      cam.worldToScreenX(g.x),
      cam.worldToScreenY(g.y) + Math.sin(t * 4 + g.bob) * 2 * z,
      (coin ? 7 : 6) * z,
      coin ? 1 : 0,
    );
    _gemCol.push(coin ? '#ffd15c' : g.value >= 12 ? '#ff5cf0' : g.value >= 5 ? '#ffd15c' : '#7df9ff');
  }
  // Pass 1 — halos, one composite switch for the whole field.
  if (_gemCol.length) {
    drawGemHalos(ctx);
    // Pass 2 — bodies, then every highlight under a single fillStyle.
    for (let i = 0, j = 0; i < _gemXYR.length; i += 4, j++) {
      const x = _gemXYR[i], y = _gemXYR[i + 1], r = _gemXYR[i + 2];
      ctx.fillStyle = _gemCol[j];
      ctx.beginPath();
      if (_gemXYR[i + 3]) { ctx.arc(x, y, r, 0, TAU); }
      else { ctx.moveTo(x, y - r); ctx.lineTo(x + r, y); ctx.lineTo(x, y + r); ctx.lineTo(x - r, y); ctx.closePath(); }
      ctx.fill();
    }
    ctx.fillStyle = 'rgba(255,255,255,0.7)';
    for (let i = 0; i < _gemXYR.length; i += 4) {
      const x = _gemXYR[i], y = _gemXYR[i + 1], r = _gemXYR[i + 2];
      ctx.fillRect(x - r * 0.3, y - r * 0.5, r * 0.4, r * 0.4);
    }
  }
  // Pass 3 — the sprite-shaped drops (a handful at most).
  for (const g of game.pickups.active) {
    if (g.dead || g.kind === 'xp' || g.kind === 'gold') continue;
    if (!cam.visible(g.x, g.y, 40)) continue;
    const sx = cam.worldToScreenX(g.x), sy = cam.worldToScreenY(g.y);
    const bob = Math.sin(t * 4 + g.bob) * 2 * z;
    if (g.kind === 'multiply') {
      pulseGlow(ctx, sx, sy + bob, 22 * z, '#ffd15c', t);
      blitSprite(ctx, cam, 'multiply', g.x, g.y, 24 * z, { anchor: 0.5, bobY: bob });
    } else if (g.kind === 'chest') {
      pulseGlow(ctx, sx, sy + bob, 22 * z, '#ffd15c', t);
      blitSprite(ctx, cam, 'chest', g.x, g.y, 26 * z, { anchor: 0.2, bobY: bob, shadow: true });
    } else if (g.kind === 'heart') {
      blitSprite(ctx, cam, 'heart', g.x, g.y, 22 * z, { anchor: 0.5, bobY: bob });
    } else if (g.kind === 'magnet') {
      blitSprite(ctx, cam, 'magnet', g.x, g.y, 22 * z, { anchor: 0.5, bobY: bob });
    } else if (g.kind === 'bomb') {
      blitSprite(ctx, cam, 'bomb', g.x, g.y, 24 * z, { anchor: 0.4, bobY: bob });
    }
  }
}

// ------------------------------------------------------------- entities
const _sortScratch = [];
function drawEntities(ctx, game) {
  const cam = game.camera;
  // Collect visible entities for Y-sort (reused array — no per-frame alloc).
  const list = _sortScratch;
  list.length = 0;
  for (const e of game.enemies.active) {
    if (e.dead) continue;
    if (!cam.visible(e.x, e.y, e.r + 60)) continue;
    list.push(e);
  }
  list.push(game.player);
  list.sort((a, b) => a.y - b.y);

  for (const ent of list) {
    if (ent === game.player) drawPlayer(ctx, game);
    else drawEnemy(ctx, game, ent);
  }
}

// Ease-out-back — spawn-in overshoot ("boing").
function easeOutBack(t) {
  const c1 = 1.70158, c3 = c1 + 1;
  return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
}

function drawEnemy(ctx, game, e) {
  const cam = game.camera;
  const targetH = e.r * 2.9 * cam.zoom;
  const bob = Math.sin(e.bob) * 2 * cam.zoom;
  // Spawn-in: scale up with a slight overshoot. Hit: squash & stretch punch.
  let grow = 1;
  if (e.spawnT > 0) {
    grow = easeOutBack(clamp01(1 - e.spawnT / (e.spawnMax || 0.3)));
    if (grow <= 0.02) return;
  }
  const sq = e.squash || 0;
  const sx = grow * (1 + sq * 0.42);
  const sy = grow * (1 - sq * 0.36);
  if (e.tintGlow) {
    pulseGlow(ctx, cam.worldToScreenX(e.x), cam.worldToScreenY(e.y) - targetH * 0.3,
      e.r * 1.8 * cam.zoom, e.tintGlow, game.elapsed);
  }
  blitSprite(ctx, cam, e.sprite, e.x, e.y, targetH, {
    anchor: 0.25, bobY: bob, flash: e.hitFlash > 0 ? Math.min(1, e.hitFlash * 6) : 0,
    frozen: e.freezeT > 0, poisoned: e.poisonT > 0, sx, sy,
    alpha: e.ghost ? 0.55 + 0.2 * Math.sin(game.elapsed * 4 + e.seed) : undefined,
  });
  // HP bar for boss / elite.
  if (e.boss || e.elite) {
    const sx = cam.worldToScreenX(e.x), sy = cam.worldToScreenY(e.y);
    const bw = e.r * 2.4 * cam.zoom;
    const yy = sy - targetH - 8 * cam.zoom;
    ctx.fillStyle = 'rgba(0,0,0,0.6)';
    ctx.fillRect(sx - bw / 2, yy, bw, 5 * cam.zoom);
    ctx.fillStyle = e.boss ? '#ff2d9b' : '#ffd15c';
    ctx.fillRect(sx - bw / 2, yy, bw * clamp01(e.hp / e.maxHp), 5 * cam.zoom);
  }
}

function drawPlayer(ctx, game) {
  const cam = game.camera;
  const p = game.player;
  const sx = cam.worldToScreenX(p.x), sy = cam.worldToScreenY(p.y);
  const targetH = p.r * 3.1 * cam.zoom;
  const bob = Math.sin(p.bob) * 2.4 * cam.zoom;
  // Element aura.
  const col = CONFIG.colors[p.element] || CONFIG.colors.neutral;
  pulseGlow(ctx, sx, sy - targetH * 0.28, p.r * 2.0 * cam.zoom, col, game.elapsed);
  const flashing = p.iframes > 0 && (Math.floor(game.elapsed * 20) % 2 === 0);
  // Lean into movement + a soft step-squash synced to the walk bob.
  const stepSq = Math.sin(p.bob * 2) * 0.025;
  blitSprite(ctx, cam, p.spriteKey, p.x, p.y, targetH, {
    anchor: 0.22, bobY: bob, flip: p.facing < 0, alpha: flashing ? 0.4 : 1,
    rot: p.lean || 0, sx: 1 - stepSq, sy: 1 + stepSq,
  });
}

// ------------------------------------------------------------- projectiles
// `bloomPass` re-renders the field into the bloom source buffer: identical
// output minus the motion-trail ghosts, which cost two path ops per bolt and
// vanish under the blur anyway — the bloom restamp is per-call-overhead-bound.
function drawProjectiles(ctx, game, bloomPass) {
  const cam = game.camera;
  ctx.save();
  ctx.globalCompositeOperation = 'lighter';
  for (const pr of game.projectiles.active) {
    if (pr.dead || !cam.visible(pr.x, pr.y, (pr.maxR || pr.r) + 40)) continue;
    const sx = cam.worldToScreenX(pr.x), sy = cam.worldToScreenY(pr.y);
    const z = cam.zoom;
    switch (pr.kind) {
      case 'aoe': {
        // Pre-rendered falloff sprite scaled to the blast — no per-projectile
        // per-frame createRadialGradient (a measured regression class here).
        const rr = pr.r * z;
        const a = clamp01(pr.life / pr.maxLife);
        ctx.globalAlpha = a;
        ctx.drawImage(falloffSprite('aoe', pr.color), sx - rr, sy - rr, rr * 2, rr * 2);
        ctx.globalAlpha = 1;
        ctx.strokeStyle = hexA(pr.color, 0.7 * a); ctx.lineWidth = 2 * z;
        ctx.beginPath(); ctx.arc(sx, sy, rr, 0, TAU); ctx.stroke();
        break;
      }
      case 'melee': {
        const reach = pr.reach * game.player.areaMult * z;
        const px = cam.worldToScreenX(game.player.x), py = cam.worldToScreenY(game.player.y);
        ctx.strokeStyle = hexA(pr.color, 0.85);
        ctx.lineWidth = 7 * z;
        ctx.beginPath();
        ctx.arc(px, py, reach * 0.7, pr.angle - pr.arcHalf, pr.angle + pr.arcHalf);
        ctx.stroke();
        ctx.strokeStyle = hexA('#ffffff', 0.5); ctx.lineWidth = 2 * z;
        ctx.beginPath();
        ctx.arc(px, py, reach * 0.7, pr.angle - pr.arcHalf, pr.angle + pr.arcHalf);
        ctx.stroke();
        break;
      }
      case 'orbit': {
        glowDot(ctx, sx, sy, pr.r * z * 1.4, pr.color);
        break;
      }
      case 'aura': {
        const rr = pr.r * z;
        const pulse = 0.5 + 0.12 * Math.sin(game.elapsed * 5);
        // Baked ring falloff; the breathing pulse rides on globalAlpha.
        ctx.globalAlpha = pulse;
        ctx.drawImage(falloffSprite('aura', pr.color), sx - rr, sy - rr, rr * 2, rr * 2);
        ctx.globalAlpha = 1;
        ctx.strokeStyle = hexA(pr.color, 0.4 * pulse); ctx.lineWidth = 2 * z;
        ctx.beginPath(); ctx.arc(sx, sy, rr, 0, TAU); ctx.stroke();
        break;
      }
      case 'zone': {
        const rr = pr.r * z;
        const a = clamp01(pr.life / pr.maxLife);
        ctx.globalAlpha = a;
        ctx.drawImage(falloffSprite('zone', pr.color), sx - rr, sy - rr, rr * 2, rr * 2);
        ctx.globalAlpha = 1;
        break;
      }
      case 'boomerang': {
        ctx.save();
        ctx.translate(sx, sy); ctx.rotate(pr.spin);
        ctx.fillStyle = hexA(pr.color, 0.9);
        const r = pr.r * z;
        ctx.fillRect(-r, -r * 0.3, r * 2, r * 0.6);
        ctx.fillRect(-r * 0.3, -r, r * 0.6, r * 2);
        ctx.restore();
        break;
      }
      case 'mine': {
        const pulse = 0.6 + 0.4 * Math.sin(game.elapsed * 10);
        glowDot(ctx, sx, sy, pr.r * z * (1 + pulse * 0.3), pr.color);
        break;
      }
      default: { // bolt / nova / homing straight — cheap, no per-bolt gradient
        ctx.save();
        ctx.translate(sx, sy);
        ctx.rotate(pr.angle);
        const r = pr.r * z * pr.drawScale;
        // Motion trail: fading ghost copies along the back-vector.
        if (!bloomPass && (pr.vx || pr.vy)) {
          ctx.fillStyle = hexA(pr.color, 0.20);
          ctx.beginPath(); ctx.ellipse(-r * 2.6, 0, r * 1.9, r * 0.72, 0, 0, TAU); ctx.fill();
          ctx.fillStyle = hexA(pr.color, 0.09);
          ctx.beginPath(); ctx.ellipse(-r * 4.8, 0, r * 1.5, r * 0.5, 0, 0, TAU); ctx.fill();
        }
        ctx.fillStyle = pr.color;                 // 'lighter' composite makes it glow
        ctx.beginPath();
        ctx.ellipse(0, 0, r * 2.0, r, 0, 0, TAU);
        ctx.fill();
        ctx.fillStyle = '#ffffff';
        ctx.beginPath(); ctx.arc(-r * 0.4, 0, r * 0.5, 0, TAU); ctx.fill();
        ctx.restore();
        break;
      }
    }
  }
  ctx.restore();
}

// Enemy fire owns a reserved visual grammar: a hard near-black contour ring
// around a hot core, drawn source-over so it cannot wash out in the additive
// bloom. No player projectile, gem or particle wears a dark outline, so
// "dark-ringed orb = it hurts" stays answerable pre-attentively even when the
// field is tiling with friendly cyan.
function drawEnemyBullets(ctx, game) {
  const cam = game.camera;
  for (const b of game.ebullets.active) {
    if (b.dead || !cam.visible(b.x, b.y, 30)) continue;
    const x = cam.worldToScreenX(b.x), y = cam.worldToScreenY(b.y);
    const r = b.r * cam.zoom * 1.4;
    ctx.fillStyle = 'rgba(8,2,16,0.9)';
    ctx.beginPath(); ctx.arc(x, y, r * 1.3, 0, TAU); ctx.fill();
    ctx.fillStyle = b.color;
    ctx.beginPath(); ctx.arc(x, y, r * 0.9, 0, TAU); ctx.fill();
    ctx.fillStyle = '#ffffff';
    ctx.beginPath(); ctx.arc(x, y, r * 0.4, 0, TAU); ctx.fill();
  }
}

// ------------------------------------------------------------- particles
// Font strings for the damage numbers, keyed by rounded pixel size. Building
// the string per particle per frame allocated once for every number on screen.
const _fontCache = new Map();
function numberFont(px) {
  let f = _fontCache.get(px);
  if (f === undefined) { f = `bold ${px}px 'Courier New', monospace`; _fontCache.set(px, f); }
  return f;
}

function drawParticles(ctx, game) {
  const cam = game.camera;
  // Sparks are additive and are by far the most numerous kind; the loop tracks
  // the composite op itself instead of save/restore-ing per particle. Draw
  // order and pixels are unchanged — only the redundant state churn is gone
  // (profiled at ~1400 particles: 9.9ms → 8.5ms median, ~14%).
  let lit = false;
  let lastFont = '';
  const normal = () => { if (lit) { ctx.globalCompositeOperation = 'source-over'; lit = false; } };
  for (const pt of game.particles.active) {
    if (pt.dead) continue;
    // Scalar transforms — particles are the most numerous pool in the game.
    const sx = cam.worldToScreenX(pt.x), sy = cam.worldToScreenY(pt.y);
    const a = clamp01(pt.life / pt.maxLife);
    if (pt.kind === 'text') {
      normal();
      // Numbers pop in oversized, then settle — big hits read instantly.
      const pop = a > 0.72 ? 1 + (a - 0.72) * 2.4 : 1;
      // 12px floor: zoomed-out phones were shrinking numbers below legibility.
      const font = numberFont(Math.max(12, Math.round(pt.size * pop * cam.zoom)));
      if (font !== lastFont) { ctx.font = font; lastFont = font; }
      ctx.globalAlpha = a;
      ctx.textAlign = 'center';
      ctx.lineWidth = 3;
      ctx.strokeStyle = 'rgba(0,0,0,0.7)';
      ctx.strokeText(pt.text, sx, sy);
      ctx.fillStyle = pt.color;
      ctx.fillText(pt.text, sx, sy);
      ctx.globalAlpha = 1;
    } else if (pt.kind === 'pop') {
      normal();
      // Death pop: the sprite expands, flashes bright and fades out.
      const spr = getSprite(pt.sprite);
      if (spr) {
        const t = 1 - a;
        const targetH = pt.size * cam.zoom * (1 + t * 0.55);
        const scale = targetH / spr.h;
        const dw = spr.w * scale, dh = spr.h * scale;
        ctx.save();
        ctx.imageSmoothingEnabled = false;
        ctx.globalAlpha = a * 0.9;
        ctx.translate(sx, sy - dh * 0.25);
        ctx.drawImage(spr.canvas, -dw / 2, -dh / 2, dw, dh);
        ctx.globalCompositeOperation = 'lighter';
        ctx.drawImage(spr.canvas, -dw / 2, -dh / 2, dw, dh);
        ctx.restore();
      }
    } else if (pt.kind === 'ring') {
      normal();
      ctx.globalAlpha = a * 0.8;
      ctx.strokeStyle = pt.color; ctx.lineWidth = 3 * cam.zoom;
      ctx.beginPath(); ctx.arc(sx, sy, pt.r * cam.zoom, 0, TAU); ctx.stroke();
      ctx.globalAlpha = 1;
    } else {
      if (!lit) { ctx.globalCompositeOperation = 'lighter'; lit = true; }
      ctx.globalAlpha = a;
      ctx.fillStyle = pt.color;
      ctx.fillRect(sx - pt.size / 2, sy - pt.size / 2, pt.size * cam.zoom, pt.size * cam.zoom);
    }
  }
  normal();
  ctx.globalAlpha = 1;
}

// ------------------------------------------------------------- bloom
// Real bloom over the faked kind: the emissive layers (projectiles, gem
// halos, additive sparks) are restamped into a quarter-resolution buffer,
// blurred with a bilinear down/up resample chain (no ctx.filter — its cost is
// wildly uneven across GPUs and headless rasterizers), and composited back
// with 'lighter'. The pre-blurred glowSprite/pulseGlow stamps are deliberately
// NOT restamped — blurring a Gaussian buys nothing — and the readability
// layers (enemy-bullet contours, damage numbers, player marker, HUD text)
// never enter the source buffer AND draw above the composite, so they stay
// crisp by construction.
//
// Buffers are keyed on CSS size only (like the vignette cache): the adaptive
// DPR governor changes the backing-store resolution mid-run, not the CSS
// size, so a governor step never rebuilds — an intentional property, bloom is
// soft by definition and quarter-CSS-res is already below any DPR tier.
const BLOOM_SCALE = 0.25;
const BLOOM_GAIN = 0.6;
let _bloomKey = '', _blA = null, _blActx = null, _blB = null, _blBctx = null;
function bloomBuffers(w, h) {
  const key = w + 'x' + h;
  if (key !== _bloomKey) {
    _bloomKey = key;
    _blA = document.createElement('canvas');
    _blA.width = Math.max(1, Math.round(w * BLOOM_SCALE));
    _blA.height = Math.max(1, Math.round(h * BLOOM_SCALE));
    _blActx = _blA.getContext('2d');
    _blB = document.createElement('canvas');
    _blB.width = Math.max(1, Math.round(_blA.width * 0.5));
    _blB.height = Math.max(1, Math.round(_blA.height * 0.5));
    _blBctx = _blB.getContext('2d');
  }
}

// Additive sparks only — text/pop/ring are excluded on purpose (damage
// numbers are a readability layer; pops are sprite-shaped, not emissive).
// Every second spark, at compensated alpha: the restamp is call-overhead-
// bound, sparks are a haze after the blur, and there can be 1400 of them.
function drawBloomSparks(ctx, game) {
  const cam = game.camera;
  const list = game.particles.active;
  ctx.save();
  ctx.globalCompositeOperation = 'lighter';
  for (let i = 0; i < list.length; i += 2) {
    const pt = list[i];
    if (pt.dead || pt.kind !== 'spark') continue;
    const sx = cam.worldToScreenX(pt.x), sy = cam.worldToScreenY(pt.y);
    ctx.globalAlpha = clamp01(pt.life / pt.maxLife * 1.5);
    ctx.fillStyle = pt.color;
    ctx.fillRect(sx - pt.size / 2, sy - pt.size / 2, pt.size * cam.zoom, pt.size * cam.zoom);
  }
  ctx.restore();
}

function drawBloom(ctx, game, w, h) {
  bloomBuffers(w, h);
  const a = _blActx, b = _blBctx;
  // 1 — restamp the emissive layers at quarter res. The camera transforms
  // hand out CSS-px coordinates, so a scale transform is the whole adapter;
  // the same draw functions render both the scene pass and the bloom source.
  a.setTransform(1, 0, 0, 1, 0, 0);
  a.clearRect(0, 0, _blA.width, _blA.height);
  a.setTransform(BLOOM_SCALE, 0, 0, BLOOM_SCALE, 0, 0);
  drawGemHalos(a);
  drawProjectiles(a, game, true);
  drawBloomSparks(a, game);
  // 2 — blur: one bilinear downsample to eighth res. The quarter-res source
  // pass anti-aliases the primitives; this tap plus the full-screen bilinear
  // upscale in step 3 gives the Gaussian-ish falloff.
  b.setTransform(1, 0, 0, 1, 0, 0);
  b.clearRect(0, 0, _blB.width, _blB.height);
  b.drawImage(_blA, 0, 0, _blB.width, _blB.height);
  // 3 — composite over the scene; the final upscale is the second blur tap.
  ctx.save();
  ctx.globalCompositeOperation = 'lighter';
  ctx.globalAlpha = BLOOM_GAIN;
  ctx.drawImage(_blB, 0, 0, w, h);
  ctx.restore();
}

// ------------------------------------------------------------- biome darkness
// Multiply-composited darkness with light cutouts, for the biomes whose stage
// declares one (`stage.darkness = { color, alpha }` — content data, the engine
// never names a biome). Drawn AFTER the ground/pickups/entities and BEFORE
// every emissive/readability layer, so darkness dims the world while lights,
// bullets and numbers stay untouched. Runs at half CSS resolution — darkness
// is low-frequency by nature — with the same size-keyed cache as bloom.
const DARK_SCALE = 0.25;
let _darkKey = '', _darkC = null, _darkCtx = null;
function darknessBuffer(w, h) {
  const key = w + 'x' + h;
  if (key !== _darkKey) {
    _darkKey = key;
    _darkC = document.createElement('canvas');
    _darkC.width = Math.max(1, Math.round(w * DARK_SCALE));
    _darkC.height = Math.max(1, Math.round(h * DARK_SCALE));
    _darkCtx = _darkC.getContext('2d');
  }
}

// Radial light stamp for cutting holes in the darkness layer — the glowSprite
// cache peaks at 0.55 alpha (it is a glow, not a hole punch); this one reaches
// 1 at the core so a light can fully clear the dark.
let _lightSprite = null;
function lightSprite() {
  if (!_lightSprite) {
    const c = document.createElement('canvas');
    c.width = c.height = 128;
    const g = c.getContext('2d');
    const grd = g.createRadialGradient(64, 64, 0, 64, 64, 64);
    grd.addColorStop(0, 'rgba(255,255,255,1)');
    grd.addColorStop(0.45, 'rgba(255,255,255,0.75)');
    grd.addColorStop(1, 'rgba(255,255,255,0)');
    g.fillStyle = grd;
    g.fillRect(0, 0, 128, 128);
    _lightSprite = c;
  }
  return _lightSprite;
}

function drawDarkness(ctx, game, w, h) {
  const dk = game.stage.darkness;
  if (!dk || w <= 0 || h <= 0) return;
  darknessBuffer(w, h);
  const g = _darkCtx;
  const cam = game.camera, z = cam.zoom;
  g.setTransform(DARK_SCALE, 0, 0, DARK_SCALE, 0, 0);
  g.globalCompositeOperation = 'source-over';
  g.globalAlpha = 1;
  g.clearRect(0, 0, w, h);
  g.fillStyle = hexA(dk.color, dk.alpha);
  g.fillRect(0, 0, w, h);
  // Light cutouts. The luminous FEW carve holes — the player's pool, treasure,
  // glowing elites, a strided sample of projectiles. The stamp budget is per-
  // call-overhead-bound in software raster (measured 13.5ms/frame when every
  // gem and enemy bullet stamped its own hole), so the dense fields are left
  // to the bloom composite, which re-lights them above the darkness anyway.
  g.globalCompositeOperation = 'destination-out';
  const stamp = lightSprite();
  const hole = (x, y, r, a) => {
    g.globalAlpha = a;
    g.drawImage(stamp, x - r, y - r, r * 2, r * 2);
  };
  const p = game.player;
  hole(cam.worldToScreenX(p.x), cam.worldToScreenY(p.y), 320 * z, 1);
  // Sprite-shaped drops and glowing enemies (bosses, elites, tinted casters).
  for (const gm of game.pickups.active) {
    if (gm.dead || gm.kind === 'xp' || gm.kind === 'gold') continue;
    if (!cam.visible(gm.x, gm.y, 40)) continue;
    hole(cam.worldToScreenX(gm.x), cam.worldToScreenY(gm.y), 70 * z, 0.9);
  }
  for (const e of game.enemies.active) {
    if (e.dead || !(e.tintGlow || e.boss || e.elite)) continue;
    if (!cam.visible(e.x, e.y, e.r + 60)) continue;
    hole(cam.worldToScreenX(e.x), cam.worldToScreenY(e.y), e.r * 4 * z, 0.75);
  }
  // Projectiles light the ground they fly over — strided so a Multiplication
  // build cannot turn the hole pass into hundreds of stamps.
  const prs = game.projectiles.active;
  const stride = prs.length > 48 ? Math.ceil(prs.length / 48) : 1;
  for (let i = 0; i < prs.length; i += stride) {
    const pr = prs[i];
    if (pr.dead || !cam.visible(pr.x, pr.y, (pr.maxR || pr.r) + 40)) continue;
    hole(cam.worldToScreenX(pr.x), cam.worldToScreenY(pr.y), Math.max(30, pr.r * 6) * z, 0.55);
  }
  g.globalAlpha = 1;
  g.globalCompositeOperation = 'source-over';
  ctx.save();
  ctx.globalCompositeOperation = 'multiply';
  ctx.drawImage(_darkC, 0, 0, w, h);
  ctx.restore();
}

// Persistent foot-ring under the player, drawn ABOVE enemies and particles so
// it survives the exact moment it is needed most: a dense horde with the
// additive bloom at full song, where the small dark sprite disappears. The
// soft light pool in drawGround dies under particles (it is 0.09 alpha,
// additive); this ring is the "you are here" marker of last resort.
function drawPlayerMarker(ctx, game) {
  const p = game.player;
  if (!p || p.hp <= 0) return;
  const cam = game.camera;
  const sx = cam.worldToScreenX(p.x), sy = cam.worldToScreenY(p.y);
  const rx = p.r * 1.6 * cam.zoom, ry = rx * 0.42;
  const yy = sy + p.r * 0.3 * cam.zoom;   // same ground plane as the shadow
  const col = CONFIG.colors[p.element] || CONFIG.colors.neutral;
  ctx.save();
  ctx.globalAlpha = 0.85;
  ctx.strokeStyle = 'rgba(0,0,0,0.6)';
  ctx.lineWidth = 4 * cam.zoom;
  ctx.beginPath(); ctx.ellipse(sx, yy, rx, ry, 0, 0, TAU); ctx.stroke();
  ctx.strokeStyle = col === CONFIG.colors.neutral ? '#e8f4ff' : col;
  ctx.lineWidth = 1.8 * cam.zoom;
  ctx.beginPath(); ctx.ellipse(sx, yy, rx, ry, 0, 0, TAU); ctx.stroke();
  ctx.restore();
}

// ------------------------------------------------------------- overlays
// The vignette is a full-screen gradient fill drawn on *every* frame, which
// profiled as the single most expensive draw in the scene after the gem and
// particle fields (4.4ms of a ~34ms heavy frame). Baking each gradient into a
// bitmap once per canvas size and blitting it costs 0.4ms — same technique as
// the glow/falloff sprites, and the same reason. Rebuilt only on resize, so
// it is two screen-sized canvases at any moment, not two per frame.
let _vigKey = '', _vigDark = null, _vigRed = null;
function vignetteLayer(w, h, px, inner, outer, from, to) {
  const c = document.createElement('canvas');
  c.width = Math.max(1, Math.round(w * px));
  c.height = Math.max(1, Math.round(h * px));
  const g = c.getContext('2d');
  g.setTransform(px, 0, 0, px, 0, 0);
  const grd = g.createRadialGradient(w / 2, h / 2, inner, w / 2, h / 2, outer);
  grd.addColorStop(0, from);
  grd.addColorStop(1, to);
  g.fillStyle = grd;
  g.fillRect(0, 0, w, h);
  return c;
}
function vignettes(ctx, w, h) {
  // Bake at device resolution so a HiDPI canvas stays pixel-exact.
  const px = ctx.getTransform ? (ctx.getTransform().a || 1) : 1;
  const key = w + 'x' + h + '@' + px;
  if (key !== _vigKey) {
    _vigKey = key;
    _vigDark = vignetteLayer(w, h, px, Math.min(w, h) * 0.35, Math.max(w, h) * 0.72,
      'rgba(0,0,0,0)', 'rgba(0,0,0,0.55)');
    _vigRed = vignetteLayer(w, h, px, Math.min(w, h) * 0.30, Math.max(w, h) * 0.62,
      'rgba(255,20,60,0)', 'rgba(255,20,60,0.5)');
  }
  return { dark: _vigDark, red: _vigRed };
}

function drawVignette(ctx, game, w, h) {
  if (w <= 0 || h <= 0) return;   // a zero-sized source would throw on drawImage
  const v = vignettes(ctx, w, h);
  ctx.drawImage(v.dark, 0, 0, w, h);
  // Danger pulse — the edges of the screen breathe red when near death.
  const p = game.player;
  const frac = p.hp / p.maxHp;
  if (frac < 0.35 && p.hp > 0) {
    const k = 1 - frac / 0.35;
    const pulse = 0.65 + 0.35 * Math.sin(game.elapsed * 5.5);
    ctx.globalAlpha = k * pulse;
    ctx.drawImage(v.red, 0, 0, w, h);
    ctx.globalAlpha = 1;
  }
}

function drawAnnounce(ctx, game, w, h, clearBelow) {
  if (!game.announce) return;
  const a = game.announce;
  const t = clamp01(a.t / a.max);
  const fade = t > 0.8 ? (1 - t) / 0.2 : t < 0.2 ? t / 0.2 : 1;
  ctx.globalAlpha = fade;
  ctx.font = `bold ${Math.round(30 * game.camera.zoom)}px 'Courier New', monospace`;
  ctx.textAlign = 'center';
  // `h * 0.26` is the designed position and it stays put wherever it already
  // clears the chrome — on desktop and portrait the line never moves. On a
  // landscape phone 0.26 of 390px lands inside the weapon row, which is why
  // ALPHA THE MAW used to read as shredded text over the slot icons; there the
  // line drops to sit under whatever the HUD and a live combo streak occupy.
  // Measuring the ascent is exact where a constant would guess, and costs
  // nothing in the common case — this function returns before it without one.
  const y = Math.max(h * 0.26, clearBelow + 12 + ctx.measureText(a.text).actualBoundingBoxAscent);
  ctx.fillStyle = 'rgba(0,0,0,0.6)';
  ctx.fillText(a.text, w / 2 + 2, y + 2);
  ctx.fillStyle = '#7df9ff';
  ctx.fillText(a.text, w / 2, y);
  ctx.globalAlpha = 1;
  ctx.textAlign = 'left';
}

// ------------------------------------------------------------- helpers
// Shared placement transform for blitSprite's two passes (draw + hit flash).
// A plain function, not a per-call closure — blitSprite runs per entity and
// per decoration per frame, so it must not allocate.
function placeSprite(ctx, x, bottom, rot, sclX, sclY, dh) {
  ctx.translate(x, bottom);
  if (rot) ctx.rotate(rot);
  ctx.scale(sclX, sclY);
  ctx.translate(0, -dh / 2);
}

// Status tints, baked once per (sprite, tint) into an offscreen canvas.
//
// These used to be a 'source-atop' fillRect straight onto the main canvas.
// source-atop clips to the DESTINATION's alpha, and the destination there is
// the scene — opaque ground and neighbouring sprites — so the fill covered the
// sprite's whole bounding box and every chilled enemy wore a blue block.
// Baking into a canvas that contains nothing but the sprite makes the
// destination alpha the sprite's own alpha, which is what the effect always
// meant. Costs one cached drawImage in place of a drawImage + fillRect, so a
// tinted enemy is now *cheaper* to draw than it was, with no per-frame alloc:
// sprites are tiny and there are two tint colours, so the cache tops out at a
// few dozen small canvases and is keyed off the sprite object (no string
// concat in the draw loop, and no staleness if sprites are ever re-rasterised).
//
// A tint is a LIST of composite passes, not a single colour. A flat wash can
// only ever push a pixel toward the wash colour, which is why poison — a green
// wash — barely showed on the green half of the roster: a slime's median pixel
// moved 10.6 dE and 14% of them moved under 10 (the point where a colour change
// stops registering at a glance), against 31 dE for a bone skeleton. Raising
// the alpha only washes the sprite out; what was missing was any change on the
// other two axes. Passes give all three, so a tint reads on ANY base:
//   'color'       recolours to the tint's hue/saturation but keeps the pixel's
//                 own LUMINOSITY, so every shading tier and the silhouette
//                 survive — this is what stops it becoming a flat blob.
//   'multiply'    drops value. Poisoned reads necrotic where chilled reads pale;
//                 that value split is what tells the two statuses apart.
//   'source-atop' lifts the deepest shadow tier so the effect still reads on
//                 near-black outline pixels.
// Every pass is baked once into the cached copy, so the draw loop is unchanged:
// still one cached drawImage per tinted entity.
const FROZEN_TINT = [{ op: 'source-atop', color: 'rgba(120,220,255,0.45)' }];
const POISON_TINT = [
  { op: 'color', color: 'rgba(168,224,36,0.80)' },
  { op: 'multiply', color: 'rgba(178,204,126,0.35)' },
  { op: 'source-atop', color: 'rgba(143,212,30,0.16)' },
];
const _tintCache = new WeakMap();
function tintedSprite(spr, tint) {
  let byTint = _tintCache.get(spr);
  if (byTint === undefined) { byTint = new Map(); _tintCache.set(spr, byTint); }
  let c = byTint.get(tint);
  if (c === undefined) {
    c = document.createElement('canvas');
    c.width = spr.w; c.height = spr.h;
    const g = c.getContext('2d');
    g.imageSmoothingEnabled = false;
    g.drawImage(spr.canvas, 0, 0);
    for (const pass of tint) {
      g.globalCompositeOperation = pass.op;
      g.fillStyle = pass.color;
      g.fillRect(0, 0, spr.w, spr.h);
    }
    // Only 'source-atop' clips itself to the destination; the blend modes
    // composite source-over, so they would leave the tint colour across the
    // transparent bounding box. Re-clipping to the sprite's own alpha makes any
    // pass list safe — and it is the whole point of baking into this canvas.
    g.globalCompositeOperation = 'destination-in';
    g.drawImage(spr.canvas, 0, 0);
    byTint.set(tint, c);
  }
  return c;
}

function blitSprite(ctx, cam, key, wx, wy, targetH, opts = {}) {
  const spr = getSprite(key);
  if (!spr) return;
  const scale = targetH / spr.h;
  const dw = spr.w * scale, dh = spr.h * scale;
  const sx = cam.worldToScreenX(wx), sy = cam.worldToScreenY(wy);
  const bobY = opts.bobY || 0;
  const anchor = opts.anchor ?? 0.25; // fraction of height below the ground point
  const bottom = sy + dh * anchor + bobY;
  if (opts.shadow) {
    ctx.fillStyle = DECOR_SHADOW;
    ctx.beginPath();
    ctx.ellipse(sx, sy + 2, dw * 0.4, dw * 0.18, 0, 0, TAU);
    ctx.fill();
  }
  // Squash/stretch anchored at the feet; optional lean rotation.
  const sclX = (opts.sx ?? 1) * (opts.flip ? -1 : 1);
  const sclY = opts.sy ?? 1;
  const rot = opts.rot || 0;
  ctx.save();
  ctx.imageSmoothingEnabled = false;
  if (opts.alpha != null) ctx.globalAlpha = opts.alpha;
  placeSprite(ctx, sx, bottom, rot, sclX, sclY, dh);
  // Status tints ride on a pre-tinted copy of the sprite, so only its own
  // pixels colour — the silhouette stays intact and the ground shows through.
  const tint = opts.frozen ? FROZEN_TINT : opts.poisoned ? POISON_TINT : null;
  ctx.drawImage(tint ? tintedSprite(spr, tint) : spr.canvas, -dw / 2, -dh / 2, dw, dh);
  ctx.restore();
  // Hit flash (brighten actual sprite pixels).
  if (opts.flash) {
    ctx.save();
    ctx.imageSmoothingEnabled = false;
    ctx.globalCompositeOperation = 'lighter';
    ctx.globalAlpha = opts.flash * 0.9;
    placeSprite(ctx, sx, bottom, rot, sclX, sclY, dh);
    ctx.drawImage(spr.canvas, -dw / 2, -dh / 2, dw, dh);
    ctx.restore();
  }
}

function glowDot(ctx, x, y, r, color) {
  // Layered flat circles (no gradient) — used for orbs, mines and enemy bullets.
  ctx.fillStyle = hexA(color, 0.5);
  ctx.beginPath(); ctx.arc(x, y, r * 1.5, 0, TAU); ctx.fill();
  ctx.fillStyle = color;
  ctx.beginPath(); ctx.arc(x, y, r * 0.9, 0, TAU); ctx.fill();
  ctx.fillStyle = '#ffffff';
  ctx.beginPath(); ctx.arc(x, y, r * 0.42, 0, TAU); ctx.fill();
}

// Pre-rendered radial glow canvases, one per color — replaces per-frame
// createRadialGradient calls (there can be one glow per enemy per frame).
const _glowCache = new Map();
function glowSprite(color) {
  let c = _glowCache.get(color);
  if (!c) {
    c = document.createElement('canvas');
    c.width = c.height = 64;
    const g = c.getContext('2d');
    const grd = g.createRadialGradient(32, 32, 0, 32, 32, 32);
    grd.addColorStop(0, hexA(color, 0.55));
    grd.addColorStop(0.55, hexA(color, 0.18));
    grd.addColorStop(1, 'rgba(0,0,0,0)');
    g.fillStyle = grd;
    g.fillRect(0, 0, 64, 64);
    _glowCache.set(color, c);
  }
  return c;
}

// Pre-rendered radial-falloff sprites for aoe blasts, auras and zones — the
// same idea as glowSprite, one canvas per (profile, color), drawn scaled with
// globalAlpha instead of a createRadialGradient per projectile per frame.
// Profiles mirror the old gradients' inner radius and color stops exactly.
const FALLOFF_PROFILES = {
  aoe: { inner: 0.2, stops: [[0, 0.5], [0.7, 0.25], [1, 0]] },
  aura: { inner: 0.3, stops: [[0, 0.05], [0.75, 0.14], [1, 0.02]] },
  zone: { inner: 0.2, stops: [[0, 0.35], [0.8, 0.18], [1, 0]] },
};
const _falloffCache = new Map();
function falloffSprite(profile, color) {
  const key = profile + ':' + color;
  let c = _falloffCache.get(key);
  if (!c) {
    const P = FALLOFF_PROFILES[profile];
    c = document.createElement('canvas');
    c.width = c.height = 128;
    const g = c.getContext('2d');
    const grd = g.createRadialGradient(64, 64, 64 * P.inner, 64, 64, 64);
    for (const [pos, a] of P.stops) grd.addColorStop(pos, hexA(color, a));
    g.fillStyle = grd;
    g.fillRect(0, 0, 128, 128);
    _falloffCache.set(key, c);
  }
  return c;
}

function pulseGlow(ctx, x, y, r, color, t) {
  const p = r * (0.85 + 0.15 * Math.sin(t * 5));
  ctx.save();
  ctx.globalCompositeOperation = 'lighter';
  ctx.globalAlpha = 0.65;
  ctx.drawImage(glowSprite(color), x - p, y - p, p * 2, p * 2);
  ctx.restore();
}

// Color utilities (accept #rgb / #rrggbb).
function parseHex(hex) {
  let h = hex.replace('#', '');
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  const n = parseInt(h, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
function hexA(hex, a) {
  if (hex.startsWith('rgba') || hex.startsWith('rgb')) return hex;
  const [r, g, b] = parseHex(hex);
  return `rgba(${r},${g},${b},${a})`;
}
function shade(hex, amt) {
  const [r, g, b] = parseHex(hex);
  const f = amt < 0 ? 1 + amt : 1 - amt;
  const add = amt > 0 ? 255 * amt : 0;
  return `rgb(${Math.round(r * f + add)},${Math.round(g * f + add)},${Math.round(b * f + add)})`;
}
